import React, { useState, useEffect } from 'react';
import { Player } from '../types';
import { isStaff } from '../utils/roles';
import { db, doc, getDoc } from '../firebaseConfig';

interface PlayerProfileProps {
  player: Player;
  onClose: () => void;
  viewerRole?: string; // PRESENCE-PRIVACY-V1: rollen hos den, der ser kortet
}

const PlayerProfile: React.FC<PlayerProfileProps> = ({ player: rawPlayer, onClose, viewerRole }) => {
  const [player, setPlayer] = useState<Player>(rawPlayer);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", rawPlayer.id));
        if (active && snap.exists()) {
          const d: any = snap.data();
          setPlayer(p => ({ ...p,
            magicalLevel: d?.level ?? d?.magicalLevel ?? p.magicalLevel,
            xp: d?.xp ?? p.xp,
            realName: d?.realName ?? p.realName,
            guild: d?.guild ?? d?.house ?? p.guild,
            groupId: d?.groupId ?? p.groupId,
            wizardName: d?.username ?? d?.wizardName ?? p.wizardName,
            avatarUrl: d?.customAvatar || d?.avatarUrl || p.avatarUrl,
          }));
        }
      } catch (e) {}
    })();
    return () => { active = false; };
  }, [rawPlayer.id]);
  const rank = player.magicalLevel > 50 ? 'Legendarisk' : player.magicalLevel > 25 ? 'Mester' : 'Lærling';

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div 
        className="w-full max-w-md bg-[#0a0a1a] border-2 border-yellow-500/30 rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,1)] relative flex flex-col items-center p-10"
        onClick={e => e.stopPropagation()}
      >
        <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-yellow-500/10 to-transparent" />
        
        <button onClick={onClose} className="absolute top-8 right-8 text-white/40 hover:text-white text-3xl">&times;</button>

        <div className="relative mt-4">
          <div className="w-32 h-32 rounded-full border-4 border-yellow-500/50 p-1 bg-indigo-950 overflow-hidden shadow-2xl">
            <img src={player.avatarUrl} alt={player.name} className="w-full h-full object-cover rounded-full" />
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
            Level {player.magicalLevel}
          </div>
        </div>

        <div className="text-center mt-8 space-y-2">
          <h2 className="text-3xl font-cinzel text-white uppercase tracking-tighter">{player.wizardName || player.name}</h2>
          <p className="text-indigo-400 font-black text-[10px] uppercase tracking-[0.3em]">{isStaff(player.role) ? 'Vogter' : 'Eventyrer'}</p>
        </div>

        <div className="w-full mt-10 space-y-4">
           <div className="bg-white/5 border border-white/5 p-6 rounded-3xl flex justify-between items-center group hover:bg-white/10 transition-all">
              <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Gilde</span>
              <span className="text-sm font-bold text-white">{player.guild || 'Ingen gilde'}</span>
           </div>
           <div className="bg-white/5 border border-white/5 p-6 rounded-3xl flex justify-between items-center group hover:bg-white/10 transition-all">
              <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Broderskab</span>
              <span className="text-sm font-bold text-white">{player.groupId || 'Solitær'}</span>
           </div>
           {isStaff(viewerRole) && ( /* PRESENCE-PRIVACY-V1: rigtige navne kun for staff (beslutningen 11/8) */
           <div className="bg-white/5 border border-white/5 p-6 rounded-3xl flex justify-between items-center group hover:bg-white/10 transition-all">
              <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Sandt Navn</span>
              <span className="text-sm font-bold text-white italic lowercase">{player.realName || 'Ukendt'}</span>
           </div>
           )}
        </div>

        <div className="mt-12 w-full">
           <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-indigo-400 mb-2 px-2">
              <span>Magisk Erfaring</span>
              <span>{player.xp ?? 0} XP</span>
           </div>
           <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5 p-0.5">
              <div 
                className="h-full bg-gradient-to-r from-yellow-600 to-yellow-400 rounded-full shadow-[0_0_10px_rgba(234,179,8,0.5)]" 
                style={{ width: `${(((player.xp ?? 0) % 1000) / 1000) * 100}%` }}
              />
           </div>
        </div>

        <p className="mt-10 text-[9px] text-center text-indigo-300/30 uppercase tracking-[0.4em] italic leading-relaxed">
          "Viddommen findes i hjertet hos den,<br/>der tør udforske mørket."
        </p>
      </div>
    </div>
  );
};

export default PlayerProfile;